import { supabase } from './supabase';

export interface CompanySettings {
  id: string;
  company_name: string;
  company_address: string;
  created_at: string;
  updated_at: string;
}

export interface CompanyInfo {
  name: string;
  address: string;
  tin: string;
  sssNumber: string;
  philHealthNumber: string;
  pagIbigNumber: string;
  secNumber?: string;
  birNumber?: string;
}

class CompanySettingsService {
  /**
   * Fetch company settings from the database
   */
  async getCompanySettings(): Promise<CompanySettings | null> {
    try {
      const { data, error } = await supabase
        .from('company_settings')
        .select('*')
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching company settings:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getCompanySettings:', error);
      return null;
    }
  }

  /**
   * Get company information formatted for payslip generation
   * Falls back to default values if no settings are found
   */
  async getCompanyInfoForPayslip(): Promise<CompanyInfo> {
    const settings = await this.getCompanySettings();
    
    return {
      name: settings?.company_name || 'Your Company Name',
      address: settings?.company_address || 'Your Company Address',
      tin: 'TIN: 000-000-000-000', // These would need to be added to company_settings table
      sssNumber: 'SSS: 00-0000000-0',
      philHealthNumber: 'PhilHealth: 00-000000000-0',
      pagIbigNumber: 'Pag-IBIG: 0000-0000-0000',
      secNumber: 'SEC: 000000000000',
      birNumber: 'BIR: 000-000-000-000'
    };
  }

  /**
   * Update company settings
   */
  async updateCompanySettings(settings: Partial<CompanySettings>): Promise<CompanySettings | null> {
    try {
      const { data, error } = await supabase
        .from('company_settings')
        .update({
          ...settings,
          updated_at: new Date().toISOString()
        })
        .eq('id', settings.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating company settings:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in updateCompanySettings:', error);
      return null;
    }
  }

  /**
   * Create new company settings
   */
  async createCompanySettings(settings: Omit<CompanySettings, 'id' | 'created_at' | 'updated_at'>): Promise<CompanySettings | null> {
    try {
      const { data, error } = await supabase
        .from('company_settings')
        .insert(settings)
        .select()
        .single();

      if (error) {
        console.error('Error creating company settings:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in createCompanySettings:', error);
      return null;
    }
  }
}

export const companySettingsService = new CompanySettingsService();